import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';

import ConfirmModal from '../../app/modals/ConfirmModal';
import WriterRegisterCampaignDetailPresenter from '../../app/writerRegisterCampaign/presenters/WriterRegisterCampaignDetailPresenter';
import WriterRegisterCampaignFormObj, {
  WRITER_REGISTER_CAMPAIGN_FIELD_KEYS,
  IWriterRegisterCampaignFields,
} from '../../app/writerRegisterCampaign/validates/writerRegisterCampaignSchema';
import Breadcrumbs, { IBreadcrumbsItem } from '../../components/Breadcrumbs';
import Card from '../../components/Card';
import LeavePageAlert from '../../components/LeavePageAlert';
import Loading from '../../components/Loading';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import {
  CreatePintoProductCampaignInput,
  PintoProductCampaign,
  PintoProductCampaignProductType,
  PintoProductCampaignStatus,
  PintoProductCampaignUserType,
  UpdatePintoProductCampaignInput,
} from '../../core/graphql/types';
import useMutaionUploadStaticFile from '../../hooks/useMutaionUploadStaticFile';
import useMutationCreatePintoWriterCampaign from '../../hooks/useMutationCreatePintoWriterCampaign';
import useMutationDeletePintoWriterCampaign from '../../hooks/useMutationDeletePintoWriterCampaign';
import useMutationPreviewPintoWriterCampaignOgImage from '../../hooks/useMutationPreviewPintoWriterCampaignOgImage';
import useMutationUpdatePintoWriterCampaign from '../../hooks/useMutationUpdatePintoWriterCampaign';
import useQueryPintoWriterCampaign from '../../hooks/useQueryPintoWriterCampaign';
import { convertFileToBase64 } from '../../utils/blob';
import useHookForm from '../../utils/form/useHookForm';

const generateUuidV4 = (): string => {
  const cryptoObj = globalThis.crypto;

  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const CreateWriterRegisterCampaignPage = () => {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const campaignId = searchParams.get('id');
  const isViewMode = searchParams.get('mode') === 'view';

  const { methods, onValidate, setValue } = useHookForm<IWriterRegisterCampaignFields>({
    objectShapeForm: WriterRegisterCampaignFormObj,
    mode: 'onChange',
  });

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewOriginalPriceThb, setPreviewOriginalPriceThb] = useState<string>('');
  const [previewEbookCoverImageUrl, setPreviewEbookCoverImageUrl] = useState<string>('');
  const [previewOgImageUrl, setPreviewOgImageUrl] = useState<string>('');
  const [isGeneratingPreviewOgImage, setIsGeneratingPreviewOgImage] = useState<boolean>(false);
  const [promoteTemplateImageFile, setPromoteTemplateImageFile] = useState<File | undefined>(undefined);
  const [templateOgImageFile, setTemplateOgImageFile] = useState<File | undefined>(undefined);
  const [bannerImageFile, setBannerImageFile] = useState<File | undefined>(undefined);
  const [campaignData, setCampaignData] = useState<PintoProductCampaign | undefined>(undefined);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState<boolean>(false);
  const [isSubmittingCampaign, setIsSubmittingCampaign] = useState<boolean>(false);

  const { mutateUpdateStaticFile, isLoadingMutateUpdateStaticFile } = useMutaionUploadStaticFile();
  const { pintoWriterCampaign, fetchPintoWriterCampaign, isLoadingPintoWriterCampaign } = useQueryPintoWriterCampaign();
  const { onMutateCreatePintoWriterCampaign, isLoadingMutateCreatePintoWriterCampaign } = useMutationCreatePintoWriterCampaign();
  const { onMutateUpdatePintoWriterCampaign, isLoadingMutateUpdatePintoWriterCampaign } = useMutationUpdatePintoWriterCampaign();
  const { onMutateDeletePintoWriterCampaign } = useMutationDeletePintoWriterCampaign();
  const { onMutatePreviewPintoWriterCampaignOgImage } = useMutationPreviewPintoWriterCampaignOgImage();

  const minimumDiscountPercentage = methods.watch(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.MINIMUM_DISCOUNT_PERCENTAGE as IWriterRegisterCampaignFields);
  const isSavingCampaign = isSubmittingCampaign || isLoadingMutateCreatePintoWriterCampaign || isLoadingMutateUpdatePintoWriterCampaign || isLoadingMutateUpdateStaticFile;

  useEffect(() => {
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.USER_TYPE as IWriterRegisterCampaignFields, PintoProductCampaignUserType.Ugc);
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY as IWriterRegisterCampaignFields, 'all');
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CONDITIONS as IWriterRegisterCampaignFields, ['']);
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG as IWriterRegisterCampaignFields, []);
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.MINIMUM_DISCOUNT_PERCENTAGE as IWriterRegisterCampaignFields, 10);
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS as IWriterRegisterCampaignFields, []);
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS as IWriterRegisterCampaignFields, []);
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.SPECIFIC_WRITER_USER_IDS as IWriterRegisterCampaignFields, []);
  }, [setValue]);

  useEffect(() => {
    if (campaignId) {
      fetchPintoWriterCampaign(campaignId);
    }
  }, [campaignId, fetchPintoWriterCampaign]);

  useEffect(() => {
    if (pintoWriterCampaign) {
      setCampaignData(pintoWriterCampaign);
    }
  }, [pintoWriterCampaign]);

  useEffect(() => {
    if (campaignData) {
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.TITLE as IWriterRegisterCampaignFields, campaignData.title);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CONDITIONS as IWriterRegisterCampaignFields, campaignData.conditions?.length ? campaignData.conditions : ['']);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_START_DATE as IWriterRegisterCampaignFields, campaignData.registrationStartedAt);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_END_DATE as IWriterRegisterCampaignFields, campaignData.registrationEndedAt);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.PERIOD_START_DATE as IWriterRegisterCampaignFields, campaignData.startedAt);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.PERIOD_END_DATE as IWriterRegisterCampaignFields, campaignData.endedAt);
      setValue(
        WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.MINIMUM_DISCOUNT_PERCENTAGE as IWriterRegisterCampaignFields,
        campaignData.minimumDiscountRatio ? Math.round(campaignData.minimumDiscountRatio * 100) : undefined,
      );
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG as IWriterRegisterCampaignFields, campaignData.tags?.length ? campaignData.tags : []);
      setValue(
        WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.USER_TYPE as IWriterRegisterCampaignFields,
        campaignData.userType === PintoProductCampaignUserType.Specific ||
          campaignData.userType === PintoProductCampaignUserType.Pgc ||
          campaignData.userType === PintoProductCampaignUserType.All
          ? PintoProductCampaignUserType.Ugc
          : campaignData.userType,
      );
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY as IWriterRegisterCampaignFields, campaignData.categoryIds?.length ? 'specific' : 'all');
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS as IWriterRegisterCampaignFields, campaignData.categoryIds || []);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS as IWriterRegisterCampaignFields, campaignData.exceptCategoryIds || []);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.SPECIFIC_WRITER_USER_IDS as IWriterRegisterCampaignFields, []);
    }
  }, [campaignData, setValue]);

  const onSelectPromoteTemplateImage = useCallback((file: File | undefined) => {
    setPromoteTemplateImageFile(file);
    // In real implementation, you would upload the file and set the URL
  }, []);

  const onSelectBannerImage = useCallback((file: File | undefined) => {
    setBannerImageFile(file);
    // In real implementation, you would upload the file and set the URL
  }, []);

  const onSelectTemplateOgImage = useCallback((file: File | undefined) => {
    setTemplateOgImageFile(file);
    // In real implementation, you would upload the file and set the URL
  }, []);

  const handlePublishClick = useCallback(async () => {
    const isValid = await onValidate();
    if (isValid) {
      setShowConfirmModal(true);
    }
  }, [onValidate]);

  const uploadStaticFile = useCallback(
    async (file?: File, fallbackUrl?: string) => {
      if (!file) return fallbackUrl || '';
      const base64 = await convertFileToBase64(file);
      const data = await mutateUpdateStaticFile({ variables: { data: base64 as string } });
      return data?.data?.uploadStaticFile?.url || fallbackUrl || '';
    },
    [mutateUpdateStaticFile],
  );

  const onSubmitCampaign = useCallback(async () => {
    if (isSavingCampaign) return;

    setShowConfirmModal(false);

    const isValid = await onValidate();
    if (!isValid) {
      return;
    }

    setIsSubmittingCampaign(true);

    const formValues = methods.getValues();

    try {
      const bannerImageUrl = await uploadStaticFile(bannerImageFile, campaignData?.bannerImageUrl);
      const exampleProductPromoteImageUrl = await uploadStaticFile(promoteTemplateImageFile, campaignData?.exampleProductPromoteImageUrl);
      const templateOgImageUrl = await uploadStaticFile(templateOgImageFile, campaignData?.templateOgImageUrl || exampleProductPromoteImageUrl);

      if (!bannerImageUrl || !exampleProductPromoteImageUrl || !templateOgImageUrl) {
        ToastComponent({
          label: 'กรุณาอัปโหลดรูปแบนเนอร์, รูปตัวอย่างโปรโมต และรูปโปรโมตแคมเปญให้ครบก่อนบันทึก',
          type: IToastType.ERROR,
        });
        return;
      }

      const tags = formValues.hashtag?.map((tag: string) => tag.trim()).filter(Boolean) || undefined;

      const conditions = formValues.conditions?.map((condition: string) => condition.trim()).filter(Boolean) || undefined;

      const categoryIds =
        formValues.category === 'specific' ? formValues.categoryIds?.map((item: number | string) => Number(item)).filter((item: number) => !Number.isNaN(item)) : undefined;
      const exceptCategoryIds = formValues.exceptCategoryIds?.map((item: number | string) => Number(item)).filter((item: number) => !Number.isNaN(item)) || undefined;
      const specificWriterUserIds = formValues.userType === PintoProductCampaignUserType.Specific ? formValues.specificWriterUserIds?.filter(Boolean) || undefined : undefined;

      if (campaignId) {
        const updateInput: UpdatePintoProductCampaignInput = {
          title: formValues.title,
          registrationStartedAt: dayjs(formValues.registerStartDate).toISOString(),
          registrationEndedAt: dayjs(formValues.registerEndDate).toISOString(),
          startedAt: dayjs(formValues.periodStartDate).toISOString(),
          endedAt: dayjs(formValues.periodEndDate).toISOString(),
          minimumDiscountRatio: formValues.minimumDiscountPercentage ? Number(formValues.minimumDiscountPercentage) / 100 : undefined,
          userType: (formValues.userType || PintoProductCampaignUserType.Ugc) as PintoProductCampaignUserType,
          productTypes: [PintoProductCampaignProductType.Ebook],
          tags,
          conditions,
          categoryIds,
          exceptCategoryIds,
          specificWriterUserIds,
          bannerImageUrl,
          exampleProductPromoteImageUrl,
          templateOgImageUrl,
        };

        const res = await onMutateUpdatePintoWriterCampaign(campaignId, updateInput);
        if (res?.errors?.length || !res?.data?.updatePintoWriterCampaign?._id) {
          throw new Error(res?.errors?.[0]?.message || 'บันทึกแคมเปญไม่สำเร็จ');
        }
        ToastComponent({ label: 'อัปเดตแคมเปญเรียบร้อยแล้ว', type: IToastType.SUCCESS });
      } else {
        const createInput: CreatePintoProductCampaignInput = {
          title: formValues.title,
          slug: generateUuidV4(),
          registrationStartedAt: dayjs(formValues.registerStartDate).toISOString(),
          registrationEndedAt: dayjs(formValues.registerEndDate).toISOString(),
          startedAt: dayjs(formValues.periodStartDate).toISOString(),
          endedAt: dayjs(formValues.periodEndDate).toISOString(),
          minimumDiscountRatio: formValues.minimumDiscountPercentage ? Number(formValues.minimumDiscountPercentage) / 100 : undefined,
          userType: (formValues.userType || PintoProductCampaignUserType.Ugc) as PintoProductCampaignUserType,
          productTypes: [PintoProductCampaignProductType.Ebook],
          tags,
          conditions,
          categoryIds,
          exceptCategoryIds,
          specificWriterUserIds,
          bannerImageUrl,
          exampleProductPromoteImageUrl,
          templateOgImageUrl,
        };

        const res = await onMutateCreatePintoWriterCampaign(createInput);
        if (res?.errors?.length || !res?.data?.createPintoWriterCampaign?._id) {
          throw new Error(res?.errors?.[0]?.message || 'บันทึกแคมเปญไม่สำเร็จ');
        }
        ToastComponent({ label: 'สร้างแคมเปญเรียบร้อยแล้ว', type: IToastType.SUCCESS });
      }
      setIsSubmitSuccess(true);
      setShowConfirmModal(false);
      history.push('/writerRegisterCampaign');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'บันทึกแคมเปญไม่สำเร็จ';
      const isRegisterStartDateValidationError = errorMessage.includes('registration_started_at_must_be_more_than_one_day_from_now');
      const isRegisterEndDateValidationError = errorMessage.includes('registration_ended_at_must_be_before_started_at');

      if (isRegisterStartDateValidationError) {
        methods.setError(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_START_DATE as IWriterRegisterCampaignFields, {
          type: 'manual',
          message: 'วันเวลาเริ่มรับสมัครต้องไม่ใช่วันปัจจุบัน',
        });
      }

      if (isRegisterEndDateValidationError) {
        methods.setError(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_END_DATE as IWriterRegisterCampaignFields, {
          type: 'manual',
          message: 'วันเวลาสิ้นสุดรับสมัครต้องก่อนวันเวลาเริ่มแคมเปญ',
        });
      }

      let toastErrorMessage = 'บันทึกแคมเปญไม่สำเร็จ';
      if (isRegisterStartDateValidationError) {
        toastErrorMessage = 'วันเวลาเริ่มรับสมัครต้องไม่ใช่วันปัจจุบัน';
      } else if (isRegisterEndDateValidationError) {
        toastErrorMessage = 'วันเวลาสิ้นสุดรับสมัครต้องก่อนวันเวลาเริ่มแคมเปญ';
      }

      ToastComponent({
        label: toastErrorMessage,
        type: IToastType.ERROR,
      });
    } finally {
      setIsSubmittingCampaign(false);
    }
  }, [
    onValidate,
    isSavingCampaign,
    methods,
    bannerImageFile,
    campaignData?.bannerImageUrl,
    campaignData?.exampleProductPromoteImageUrl,
    campaignData?.templateOgImageUrl,
    campaignId,
    history,
    onMutateCreatePintoWriterCampaign,
    onMutateUpdatePintoWriterCampaign,
    promoteTemplateImageFile,
    setShowConfirmModal,
    templateOgImageFile,
    uploadStaticFile,
  ]);

  const onCancelCampaign = useCallback(async () => {
    if (!campaignId) return;

    setShowCancelModal(false);

    const res = await onMutateDeletePintoWriterCampaign(campaignId);
    if (res?.data?.deletePintoWriterCampaign?.success) {
      ToastComponent({ label: 'ลบแคมเปญเรียบร้อยแล้ว', type: IToastType.SUCCESS });
    } else {
      ToastComponent({ label: res?.data?.deletePintoWriterCampaign?.message || 'ลบแคมเปญไม่สำเร็จ', type: IToastType.ERROR });
      return;
    }

    setIsSubmitSuccess(true);
    setTimeout(() => {
      history.push('/writerRegisterCampaign');
    }, 1500);
  }, [campaignId, history, onMutateDeletePintoWriterCampaign]);

  const onOpenPreviewModal = useCallback(() => {
    setPreviewOgImageUrl('');
    setShowPreviewModal(true);
  }, []);

  const onClosePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewOgImageUrl('');
    setPreviewOriginalPriceThb('');
    setPreviewEbookCoverImageUrl('');
  }, []);

  const onGeneratePreviewOgImage = useCallback(async () => {
    const discountPercentage = Number(minimumDiscountPercentage);
    const originalPrice = Number(previewOriginalPriceThb);

    if (!discountPercentage || Number.isNaN(discountPercentage) || discountPercentage <= 0) {
      ToastComponent({ label: 'กรุณาระบุส่วนลดขั้นต่ำให้ถูกต้อง', type: IToastType.ERROR });
      return;
    }

    if (!originalPrice || Number.isNaN(originalPrice) || originalPrice <= 0) {
      ToastComponent({ label: 'กรุณากรอกราคาให้ถูกต้อง', type: IToastType.ERROR });
      return;
    }

    if (!previewEbookCoverImageUrl?.trim()) {
      ToastComponent({ label: 'กรุณากรอก URL รูปปกหนังสือ', type: IToastType.ERROR });
      return;
    }

    try {
      new URL(previewEbookCoverImageUrl);
    } catch {
      ToastComponent({ label: 'URL รูปปกหนังสือไม่ถูกต้อง', type: IToastType.ERROR });
      return;
    }

    try {
      setIsGeneratingPreviewOgImage(true);
      const templateOgImageUrl = await uploadStaticFile(templateOgImageFile, campaignData?.templateOgImageUrl);

      if (!templateOgImageUrl) {
        ToastComponent({ label: 'กรุณาเลือกรูปโปรโมตแคมเปญสำหรับ OG ก่อนพรีวิว', type: IToastType.ERROR });
        return;
      }

      const res = await onMutatePreviewPintoWriterCampaignOgImage({
        discountRatio: discountPercentage / 100,
        ebookOriginalPriceThb: originalPrice,
        ebookCoverImageUrl: previewEbookCoverImageUrl.trim(),
        templateOgImageUrl,
      });

      const previewOgImage = res?.data?.previewPintoWriterCampaignOgImage || '';

      if (!previewOgImage) {
        ToastComponent({ label: 'ไม่พบรูปพรีวิวจากระบบ', type: IToastType.ERROR });
        return;
      }

      setPreviewOgImageUrl(previewOgImage);
    } catch {
      ToastComponent({ label: 'พรีวิวรูป OG ไม่สำเร็จ', type: IToastType.ERROR });
    } finally {
      setIsGeneratingPreviewOgImage(false);
    }
  }, [
    campaignData?.templateOgImageUrl,
    minimumDiscountPercentage,
    onMutatePreviewPintoWriterCampaignOgImage,
    previewEbookCoverImageUrl,
    previewOriginalPriceThb,
    templateOgImageFile,
    uploadStaticFile,
  ]);

  const isReadOnly = isViewMode || (!!campaignData && campaignData.status !== PintoProductCampaignStatus.Pending);
  const isCategoryEditableInEditMode = !isViewMode;
  const canSubmitChanges = !isReadOnly || isCategoryEditableInEditMode;
  const hasTemplateOgImage = !!templateOgImageFile || !!campaignData?.templateOgImageUrl;
  const canPreviewOgImage = !isReadOnly && hasTemplateOgImage && !!minimumDiscountPercentage && Number(minimumDiscountPercentage) > 0;
  const isFormDirty = methods.formState.isDirty;
  const hasPendingImageChanges = !!promoteTemplateImageFile || !!templateOgImageFile || !!bannerImageFile;
  const shouldShowLeavePageAlert = !isViewMode && !isSubmitSuccess && (isFormDirty || hasPendingImageChanges);

  const breadcrumbItems: IBreadcrumbsItem[] = useMemo(() => {
    const items: IBreadcrumbsItem[] = [{ name: 'แคมเปญ Writer Register', url: '/writerRegisterCampaign' }];
    if (campaignId) {
      items.push({ name: isViewMode ? 'ดูแคมเปญ' : 'แก้ไขแคมเปญ' });
    } else {
      items.push({ name: 'สร้างแคมเปญ' });
    }
    return items;
  }, [campaignId, isViewMode]);

  if (isLoadingPintoWriterCampaign) {
    return (
      <div className="w-full">
        <div className="w-full btn btn-outline btn-disabled bg-systemGrays04LabelQuaternary border-none loading h-[100px]" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {isSavingCampaign && <Loading />}

      <LeavePageAlert showConfirmLeavePageAlert={shouldShowLeavePageAlert} />

      {showConfirmModal && (
        <ConfirmModal
          title={campaignId ? 'อัปเดตแคมเปญ' : 'สร้างแคมเปญ'}
          subTitle={`คุณแน่ใจหรือไม่ว่าต้องการ${campaignId ? 'อัปเดต' : 'สร้าง'}แคมเปญนี้?`}
          onConFirm={onSubmitCampaign}
          onClose={() => setShowConfirmModal(false)}
          confirmText={campaignId ? 'อัปเดต' : 'สร้าง'}
          cancelText="ยกเลิก"
        />
      )}

      {showCancelModal && (
        <ConfirmModal
          title="ยกเลิกแคมเปญ"
          subTitle="คุณแน่ใจหรือไม่ว่าต้องการยกเลิกแคมเปญนี้? การกระทำนี้ไม่สามารถย้อนกลับได้"
          onConFirm={onCancelCampaign}
          onClose={() => setShowCancelModal(false)}
          confirmText="ยืนยันยกเลิกแคมเปญ"
          cancelText="ย้อนกลับ"
        />
      )}

      {showPreviewModal && (
        <div className="modal modal-open">
          <div className="modal-box relative max-w-[720px]">
            <h3 className="text-lg font-bold font-tt text-systemGrays01LabelPrimary">พรีวิว OG Image</h3>
            <p className="font-tt pt-8 text-colorsBrandWarmBlack01LabelPrimary">กรอกราคาและ URL รูปปกหนังสือเพื่อสร้างภาพพรีวิว</p>

            <div className="pt-16 space-y-12">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">ราคา (บาท)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  className="input input-bordered"
                  placeholder="เช่น 299"
                  value={previewOriginalPriceThb}
                  onChange={(e) => setPreviewOriginalPriceThb(e.target.value)}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">URL รูปปกหนังสือ</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered"
                  placeholder="https://example.com/ebook-cover.jpg"
                  value={previewEbookCoverImageUrl}
                  onChange={(e) => setPreviewEbookCoverImageUrl(e.target.value)}
                />
              </div>

              <div className="w-full flex justify-end space-x-6">
                <button className="btn btn-outline btn-error" onClick={onClosePreviewModal}>
                  ปิด
                </button>
                <button className="btn btn-error" onClick={onGeneratePreviewOgImage} disabled={isGeneratingPreviewOgImage}>
                  {isGeneratingPreviewOgImage ? 'กำลังพรีวิว...' : 'พรีวิว'}
                </button>
              </div>

              {previewOgImageUrl && (
                <div className="pt-4">
                  <div className="text-sm text-systemGrays02LabelSecondary mb-8">ภาพตัวอย่างจาก API</div>
                  <img className="w-full rounded-8 border border-base-200" src={previewOgImageUrl} alt="preview-og-image" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <>
        <div className="sticky top-0 z-10 py-8 bg-componentsBgGrouped01">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex justify-between pt-5">
            <h1 className="font-dbh text-[28px]">{campaignId ? (isViewMode ? 'ดูแคมเปญ' : 'แก้ไขแคมเปญ') : 'สร้างแคมเปญใหม่'}</h1>

            <div className="space-x-12">
              <Link to="/writerRegisterCampaign">
                <button className="btn btn-outline">{isReadOnly ? 'ย้อนกลับ' : 'ยกเลิก'}</button>
              </Link>

              {!isReadOnly && (
                <button className="btn btn-outline" onClick={onOpenPreviewModal} disabled={!canPreviewOgImage || isSavingCampaign}>
                  พรีวิว
                </button>
              )}

              {canSubmitChanges && (
                <button className={isSavingCampaign ? 'btn btn-error loading' : 'btn btn-error'} onClick={handlePublishClick} disabled={isSavingCampaign}>
                  {isSavingCampaign ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
              )}

              {campaignData && !isViewMode && (
                <button className="btn btn-outline btn-error" onClick={() => setShowCancelModal(true)}>
                  ลบแคมเปญ
                </button>
              )}
            </div>
          </div>

          <div className="mb-0 divider opacity-80" />
        </div>

        <Card className="flex flex-row w-full">
          <div className="flex-1">
            <FormProvider {...methods}>
              <WriterRegisterCampaignDetailPresenter
                status={campaignData?.status}
                readOnly={isReadOnly}
                categoryReadOnly={isViewMode}
                promoteTemplateImageFile={promoteTemplateImageFile}
                promoteTemplateImageUrl={campaignData?.exampleProductPromoteImageUrl}
                templateOgImageFile={templateOgImageFile}
                templateOgImageUrl={campaignData?.templateOgImageUrl}
                bannerImageFile={bannerImageFile}
                bannerImageUrl={campaignData?.bannerImageUrl}
                onSelectPromoteTemplateImage={onSelectPromoteTemplateImage}
                onSelectTemplateOgImage={onSelectTemplateOgImage}
                onSelectBannerImage={onSelectBannerImage}
              />
            </FormProvider>
          </div>
        </Card>
      </>
    </div>
  );
};

export default CreateWriterRegisterCampaignPage;
